import React, { useState, useEffect, useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { FaBell, FaExclamationCircle, FaCog, FaCheckCircle, FaInbox, FaTimes } from 'react-icons/fa'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { useNavigate } from 'react-router-dom'
import api from '@/api/config'
import './NotificationCenter.scss'
import Tooltip from '../Tooltip'

interface Notification {
  id: number
  uuid: string
  type: 'email_reauth' | 'campaign_processing' | 'campaign_completed' | 'new_reply'
  title: string
  message: string
  metadata: any
  is_read: boolean
  created_at: string
  action_url: string
  priority: 'high' | 'normal' | 'low'
}

const NotificationCenter: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  // Fetch notifications
  const { data: notificationData, refetch } = useQuery(
    'notifications',
    async () => {
      const response = await api.get('/api/notifications', {
        params: { limit: 20 }
      })
      return response.data
    },
    {
      refetchInterval: 30000, // Refresh every 30 seconds
    }
  )

  // Fetch unread count separately for badge
  const { data: unreadData } = useQuery(
    'unreadNotifications',
    async () => {
      const response = await api.get('/api/notifications/unread')
      return response.data
    },
    {
      refetchInterval: 30000,
    }
  )

  const notifications = notificationData?.notifications || []
  const unreadCount = unreadData?.unread_count || 0

  // Mark as read mutation
  const markAsReadMutation = useMutation(
    (uuid: string) => api.post(`/api/notifications/${uuid}/read`),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('notifications')
        queryClient.invalidateQueries('unreadNotifications')
      }
    }
  )

  // Mark all as read mutation
  const markAllAsReadMutation = useMutation(
    () => api.post('/api/notifications/mark-all-read'),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('notifications')
        queryClient.invalidateQueries('unreadNotifications')
      }
    }
  )

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleNotificationClick = (notification: Notification) => {
    // Mark as read if not already
    if (!notification.is_read) {
      markAsReadMutation.mutate(notification.uuid)
    }

    // Close dropdown
    setIsOpen(false)

    // Navigate based on action_url
    if (notification.action_url) {
      navigate(notification.action_url)
    }
  }

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'email_reauth':
        return <FaExclamationCircle className="icon-danger" />
      case 'campaign_processing':
        return <FaCog className="icon-warning" />
      case 'campaign_completed':
        return <FaCheckCircle className="icon-success" />
      case 'new_reply':
        return <FaInbox className="icon-info" />
      default:
        return <FaBell />
    }
  }

  const formatTime = (dateString: string) => {
    const date = new Date(dateString)
    const now = new Date()
    const diff = now.getTime() - date.getTime()
    const minutes = Math.floor(diff / 60000)
    const hours = Math.floor(diff / 3600000)
    const days = Math.floor(diff / 86400000)

    if (minutes < 1) return 'Just now'
    if (minutes < 60) return `${minutes}m ago`
    if (hours < 24) return `${hours}h ago`
    if (days < 7) return `${days}d ago`
    
    return date.toLocaleDateString()
  }

  return (
    <div className="notification-center" ref={dropdownRef}>
      <Tooltip content="Notifications" position="right">
      <button
        className="notification-trigger"
        onClick={() => setIsOpen(!isOpen)}
        aria-label="Notifications"
      >
        <FaBell />
        {unreadCount > 0 && (
          <span className="notification-badge">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>
      </Tooltip>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            className="notification-dropdown"
            initial={{ opacity: 0, y: -10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.95 }}
            transition={{ duration: 0.2 }}
          >
            <div className="dropdown-header">
              <h3>Notifications</h3>
              {unreadCount > 0 && (
                <button
                  className="mark-all-read"
                  onClick={() => markAllAsReadMutation.mutate()}
                >
                  Mark all as read
                </button>
              )}
            </div>

            <div className="notification-list">
              {notifications.length === 0 ? (
                <div className="empty-state">
                  <FaBell className="empty-icon" />
                  <p>No notifications yet</p>
                  <span>You're all caught up!</span>
                </div>
              ) : (
                notifications.map((notification: Notification, index: number) => (
                  <motion.div
                    key={notification.uuid}
                    className={`notification-item ${!notification.is_read ? 'unread' : ''} priority-${notification.priority}`}
                    onClick={() => handleNotificationClick(notification)}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.05 }}
                  >
                    <div className="notification-icon">
                      {getNotificationIcon(notification.type)}
                    </div>
                    <div className="notification-content">
                      <h4>{notification.title}</h4>
                      <p>{notification.message}</p>
                      <span className="notification-time">
                        {formatTime(notification.created_at)}
                      </span>
                    </div>
                    {!notification.is_read && (
                      <div className="unread-indicator"></div>
                    )}
                  </motion.div>
                ))
              )}
            </div>

            {notifications.length > 0 && (
              <div className="dropdown-footer">
                <button
                  className="view-all"
                  onClick={() => {
                    // Keep dropdown open to view all notifications
                    // In the future, could navigate to a dedicated page
                  }}
                >
                  Showing recent notifications
                </button>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

export default NotificationCenter
